import { z } from 'zod';

import { toHex } from 'shared-client/util';
import { wrapper } from './user-manager.js';

const ConfigJsonResponseBody = z.object({
  apiBaseUrl: z.string().url(),
  anonyClientUrl: z.string().url(),
});

export type ApiClientConfig = z.infer<typeof ConfigJsonResponseBody>;

export type PutDekRequestBody = {
  encryptedDek: Uint8Array<ArrayBuffer>;
  fileContentType: string;
  fileName: string;
  fileSize: number;
};

const WordListResponseBody = z.object({
  list: z.array(z.string()),
});

const PutDekResponseBody = z.object({
  url: z.string().url(),
  fields: z.record(z.string()),
});
export type PutDekResponseBody = z.infer<typeof PutDekResponseBody>;

export type AuthenticateError = {
  type: 'authenticate';
};

export type PutDekOutput = {
  ok: true;
  value: PutDekResponseBody;
} | {
  ok: false;
  error: AuthenticateError;
};

export class ApiClient {

  constructor(
    private readonly config: ApiClientConfig,
    private readonly http: typeof fetch = fetch.bind(window) // Use the global fetch function
  ) {
  }

  get baseUrl(): string {
    return this.config.apiBaseUrl;
  }

  anonyClientUrl(salt: string): string {
    return this.config.anonyClientUrl.replace('$salt', salt);
  }

  static async fromConfigJson(http: typeof fetch = fetch.bind(window)): Promise<ApiClient> {
    const response = await http('config.json');
    if (!response.ok) {
      throw new Error(`Failed to fetch config: ${response.statusText}`);
    }
    const body = await response.json();
    const config = ConfigJsonResponseBody.parse(body);
    return new ApiClient(config, http);
  }

  async getWordList(): Promise<string[]> {
    const response = await this.http('list.json');
    if (!response.ok) {
      throw new Error(`Failed to fetch word list: ${response.statusText}`);
    }
    const data = await response.json();
    const wordList = WordListResponseBody.parse(data);
    return wordList.list;
  }

  async putDek(
    secureContentsId: string,
    {
      encryptedDek,
      fileContentType,
      fileName,
      fileSize,
    }: PutDekRequestBody
  ): Promise<PutDekOutput> {
    const user = await wrapper.getUser();
    if (!user) {
      return {
        ok: false,
        error: {
          type: 'authenticate',
        },
      };
    }

    const endpoint = `${this.baseUrl}/secure-contents/${secureContentsId}/dek`;
    const response = await this.http(endpoint, {
      method: 'PUT',
      headers: {
        'content-type': 'application/json',
        'authorization': `${user.token_type} ${user.access_token}`,
      },
      body: JSON.stringify({
        encryptedDek: toHex(encryptedDek),
        fileContentType,
        fileName,
        fileSize,
      }),
    });

    if (!response.ok) {
      if (response.status === 401) {
        return {
          ok: false,
          error: {
            type: 'authenticate',
          },
        };
      }
      throw new Error(`Failed to put DEK: ${response.statusText}`);
    }

    const data = await response.json();
    const value = PutDekResponseBody.parse(data);
    return {
      ok: true,
      value,
    };
  }
}
